import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { QueryKey } from '@/Config/api/queryKey';
import { showDiscordGuildConflictToast } from '@/Domain/components/Integration/Discord/DiscordGuildConflictToast';
import { STACKED_ACTION_TOAST_CLASS_NAMES } from '@/Domain/components/Shared/Toast/Toast';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import {
  trackIntegrationConnectFailed,
  trackIntegrationConnectStarted,
  trackIntegrationConnectSucceeded,
} from '@/Domain/hooks/Integration/integrationConnectAnalytics';

export const OAUTH_CALLBACK_INTEGRATIONS = ['hubspot', 'intercom', 'discord', 'instagram'] as const;
export type OAuthCallbackIntegration = (typeof OAUTH_CALLBACK_INTEGRATIONS)[number];

export const OAUTH_STATUS_CONNECTED = 'connected';
export const OAUTH_CANCELLED_REASON = 'cancelled';
// Meta's callbacks name the same outcome differently; a cancel is not an error toast.
const OAUTH_CANCELLED_REASONS = new Set([OAUTH_CANCELLED_REASON, 'user_cancelled']);
const NO_EXTRA_OAUTH_PARAMS: readonly string[] = [];
const DISCORD_OAUTH_PARAMS: readonly string[] = ['discord_nickname_status', 'agent', 'move'];
const DISCORD_GUILD_ALREADY_CONNECTED_REASON = 'guild_already_connected';
const DISCORD_GUILD_MOVABLE = '1';
const ACTION_TOAST_DURATION_MS = 15_000;

const OAUTH_CALLBACK_PARAMS = new Set<string>([
  ...OAUTH_CALLBACK_INTEGRATIONS.map((integration) => `${integration}_integration_status`),
  'whatsapp_integration_status',
  'whatsapp_attempt_id',
  'whatsapp_dialog',
  'reason',
  ...DISCORD_OAUTH_PARAMS,
]);

// Preserve only the OAuth callback params when the route guard redirects.
export const pickOAuthCallbackSearch = (search: string): string => {
  const kept = new URLSearchParams();
  new URLSearchParams(search).forEach((value, key) => {
    if (OAUTH_CALLBACK_PARAMS.has(key)) kept.append(key, value);
  });
  const query = kept.toString();
  return query ? `?${query}` : '';
};

export interface OAuthRedirectCallbackResult {
  status: string;
  reason: string | null;
  searchParams: URLSearchParams;
}

export const useOAuthRedirectCallback = (
  statusParam: string,
  onResult: (result: OAuthRedirectCallbackResult) => void,
  extraParams: readonly string[] = NO_EXTRA_OAUTH_PARAMS,
  onHandled?: () => void,
): void => {
  const [searchParams, setSearchParams] = useSearchParams();
  const handledRef = useRef(false);
  const status = searchParams.get(statusParam);
  const reason = searchParams.get('reason');

  useEffect(() => {
    if (!status || handledRef.current) return;
    handledRef.current = true;
    onResult({ status, reason, searchParams });
    onHandled?.();
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(statusParam);
        next.delete('reason');
        extraParams.forEach((param) => next.delete(param));
        return next;
      },
      { replace: true },
    );
  }, [
    status,
    reason,
    searchParams,
    statusParam,
    onResult,
    onHandled,
    extraParams,
    setSearchParams,
  ]);
};

export const useIntegrationOAuthCallback = (
  integration: OAuthCallbackIntegration,
  onResult: () => void,
  onOpenDiscordAdvanced?: () => void,
): void => {
  const { t } = useTranslation(['integration']);
  const { capture } = usePostHogAnalytics();
  const queryClient = useQueryClient();
  const statusParam = `${integration}_integration_status`;
  const { mutate: startDiscordServerMove } = useMutation<{ url: string }, Error, { move: boolean }>(
    {
      mutationKey: [QueryKey.integration.discordOauth],
      onSuccess: (result) => {
        if (!result?.url) {
          trackIntegrationConnectFailed(capture, 'discord', 'oauth_failed');
          toast.error(t('integration:discord.oneclick.oauth_failed'));
          return;
        }
        window.location.href = result.url;
      },
      onError: () => {
        trackIntegrationConnectFailed(capture, 'discord', 'oauth_failed');
        toast.error(t('integration:discord.oneclick.oauth_failed'));
      },
    },
  );
  const handleResult = useCallback(
    ({ status, reason, searchParams }: OAuthRedirectCallbackResult) => {
      const nicknameStatus =
        integration === 'discord' ? searchParams.get('discord_nickname_status') : null;
      if (status === OAUTH_STATUS_CONNECTED) {
        // Full-page redirects destroy the in-memory connect-funnel session.
        trackIntegrationConnectSucceeded(capture, integration);
        toast.success(t(`integration:${integration}.oauth_callback.success`));
        queryClient.invalidateQueries({ queryKey: [QueryKey.integration.get, integration] });
        queryClient.invalidateQueries({ queryKey: ['integrations'] });
        if (integration === 'hubspot') {
          // token-status carries the routing-side bot email the sheet displays
          queryClient.invalidateQueries({ queryKey: [QueryKey.integration.hubspotTokenStatus] });
          queryClient.invalidateQueries({ queryKey: [QueryKey.integration.hubspotPipelines] });
        }
        if (integration === 'discord') {
          // Refresh the connected-servers list and the token-status the AI Action gate reads.
          queryClient.invalidateQueries({ queryKey: [QueryKey.integration.discordGuilds] });
          queryClient.invalidateQueries({ queryKey: [QueryKey.integration.discordTokenStatus] });
          if (nicknameStatus === 'applied' || nicknameStatus === 'cleared') {
            capture(PostHogEventEnum.DiscordIntegrationNicknameSaved, {
              cleared: nicknameStatus === 'cleared',
              source: 'oauth',
            });
          } else if (nicknameStatus === 'preserved') {
            capture(PostHogEventEnum.DiscordIntegrationNicknamePreserved);
            toast.info(t('integration:discord.oneclick.nickname_preserved'));
          } else if (nicknameStatus === 'permission_failed') {
            toast.warning(t('integration:discord.oneclick.nickname_permission_failed'));
          } else if (nicknameStatus === 'failed') {
            toast.warning(t('integration:discord.oneclick.nickname_failed'));
          }
        }
      } else {
        trackIntegrationConnectFailed(capture, integration, reason ?? 'unknown');
        const reasonKey = reason ?? 'default';
        const genericMessage = t(`integration:${integration}.oauth_callback.failure.${reasonKey}`, {
          defaultValue: t(`integration:${integration}.oauth_callback.failure.default`),
        });
        // Discord names the Agent already holding the server, but only when the caller's org can see it.
        const agent = integration === 'discord' ? searchParams.get('agent') : null;
        const message = agent
          ? t(`integration:${integration}.oauth_callback.failure.${reasonKey}_named`, {
              agent,
              defaultValue: genericMessage,
            })
          : genericMessage;
        if (reason !== null && OAUTH_CANCELLED_REASONS.has(reason)) {
          toast.info(message);
        } else if (integration === 'discord' && reason === DISCORD_GUILD_ALREADY_CONNECTED_REASON) {
          // The backend offers a move only when the caller can edit the AI Agent holding the server.
          if (searchParams.get('move') === DISCORD_GUILD_MOVABLE) {
            showDiscordGuildConflictToast({
              title: message,
              onMove: () => {
                capture(PostHogEventEnum.DiscordIntegrationServerMoveClicked);
                trackIntegrationConnectStarted(capture, 'discord');
                startDiscordServerMove({ move: true });
              },
              onOpenAdvanced: onOpenDiscordAdvanced,
            });
            // The choice is the next step; a modal sheet opened by onResult would cover and block it.
            return;
          }
          const description = t(
            'integration:discord.oauth_callback.failure.guild_already_connected_description',
          );
          if (onOpenDiscordAdvanced) {
            toast.error(message, {
              description,
              action: {
                label: t(
                  'integration:discord.oauth_callback.failure.guild_already_connected_action',
                ),
                onClick: onOpenDiscordAdvanced,
              },
              duration: ACTION_TOAST_DURATION_MS,
              classNames: STACKED_ACTION_TOAST_CLASS_NAMES,
            });
            // The action is the next step; a modal sheet opened by onResult would cover and block it.
            return;
          }
          toast.error(message, { description });
        } else {
          toast.error(message);
        }
      }
      onResult();
    },
    [capture, integration, onOpenDiscordAdvanced, onResult, queryClient, startDiscordServerMove, t],
  );
  useOAuthRedirectCallback(
    statusParam,
    handleResult,
    integration === 'discord' ? DISCORD_OAUTH_PARAMS : NO_EXTRA_OAUTH_PARAMS,
  );
};
